import jsPDF from "jspdf";
import { PlusJakartaSansFontBase64String } from "../../Helpers/PlusJakartaSans-base64String";
import { imgData } from "../../Helpers/smestLogoBase64ImgData";
import { getLiquidityIndicatorDisplay } from "./liquidityIndicator";
import type { ILiquidityIndicator } from "./liquidityIndicator";

export interface LiquidityIndicatorSectionOptions {
  startY?: number;
  margin?: number;
  headerHeight?: number;
}

const registerFont = (doc: jsPDF) => {
  if (!PlusJakartaSansFontBase64String) return "helvetica";
  doc.addFileToVFS(
    "PlusJakartaSans-VariableFont_wght-normal.ttf",
    PlusJakartaSansFontBase64String
  );
  doc.addFont(
    "PlusJakartaSans-VariableFont_wght-normal.ttf",
    "PlusJakartaSans",
    "normal"
  );
  doc.addFont(
    "PlusJakartaSans-VariableFont_wght-normal.ttf",
    "PlusJakartaSans",
    "bold"
  );
  return "PlusJakartaSans";
};

const drawCompanyLogo = (doc: jsPDF, margin: number) => {
  if (!imgData) return;
  const dataUrl = imgData.startsWith("data:")
    ? imgData
    : `data:image/png;base64,${imgData}`;
  const { width, height } =
    typeof doc.getImageProperties === "function"
      ? doc.getImageProperties(dataUrl)
      : { width: 140, height: 48 };
  const scale = Math.min(140 / width, 48 / height);
  const drawWidth = width * scale;
  const drawHeight = height * scale;
  doc.addImage(
    dataUrl,
    "PNG",
    doc.internal.pageSize.getWidth() - margin - drawWidth,
    margin - 6,
    drawWidth,
    drawHeight
  );
};

/** Adds the optional BSE liquidity snapshot as one complete PDF section. */
export const generateLiquidityIndicatorSection = (
  doc: jsPDF,
  liquidityIndicator: ILiquidityIndicator,
  opts: LiquidityIndicatorSectionOptions = {}
): { doc: jsPDF; nextY: number } => {
  const margin = opts.margin ?? (doc as any).__margin ?? 36;
  const headerHeight = opts.headerHeight ?? (doc as any).__headerHeight ?? 42;
  const fullWidth = doc.internal.pageSize.getWidth() - 2 * margin;
  const pageHeight = doc.internal.pageSize.getHeight();
  const font = registerFont(doc);
  const display = getLiquidityIndicatorDisplay(liquidityIndicator);
  const metrics = [
    {
      value: display.todayTransactions,
      label: "Transaction Count",
      period: `as on ${display.asOnDate}`,
      unit: "",
    },
    {
      value: display.todayVolumeLakhs,
      label: "Volume Traded",
      period: `as on ${display.asOnDate}`,
      unit: "(in Lakhs)",
    },
    {
      value: display.last3mTransactions,
      label: "Transaction Count",
      period: "(since 3 months)",
      unit: "",
    },
    {
      value: display.last3mVolumeLakhs,
      label: "Volume Traded",
      period: "(since 3 months)",
      unit: "(in Lakhs)",
    },
  ];

  const padding = 16;
  const cardsWidth = fullWidth - 2 * padding;
  const gap = 12;
  const compactWidth = (cardsWidth - 3 * gap) / 4;
  doc.setFont(font, "bold");
  doc.setFontSize(18);
  // Keep four cards in one row for the usual values, and give large values
  // two columns rather than shrinking the text below a readable size.
  const columns = metrics.every(
    ({ value }) => doc.getTextWidth(value) <= compactWidth - 20
  )
    ? 4
    : 2;
  const cardWidth = (cardsWidth - (columns - 1) * gap) / columns;
  const cardHeight = 114;
  const rows = Math.ceil(metrics.length / columns);
  const cardsHeight = rows * cardHeight + (rows - 1) * gap;
  const sectionHeight = 36 + cardsHeight + 64;
  let y = Math.max(
    opts.startY ?? (doc as any).__nextY ?? margin + headerHeight + 8,
    margin + headerHeight + 8
  );

  if (y + sectionHeight > pageHeight - margin) {
    doc.addPage("a4");
    drawCompanyLogo(doc, margin);
    y = margin + headerHeight + 8;
  }

  doc.setLineWidth(0.6);
  doc.setDrawColor(220);
  doc.roundedRect(margin, y, fullWidth, sectionHeight, 10, 10, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(45, 51, 73);
  doc.text("Liquidity Indicator #", margin + padding, y + 22);

  const cardsY = y + 36;
  metrics.forEach((metric, index) => {
    const x = margin + padding + (index % columns) * (cardWidth + gap);
    const cardY = cardsY + Math.floor(index / columns) * (cardHeight + gap);
    const centerX = x + cardWidth / 2;

    doc.setLineWidth(0.6);
    doc.setDrawColor(225);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, cardY, cardWidth, cardHeight, 10, 10, "FD");

    doc.setFont(font, "bold");
    doc.setFontSize(26);
    const valueWidth = doc.getTextWidth(metric.value);
    doc.setFontSize(Math.min(26, (26 * (cardWidth - 20)) / valueWidth));
    doc.setTextColor(54, 43, 164);
    doc.text(metric.value, centerX, cardY + 34, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.4);
    doc.setTextColor(45, 51, 73);
    doc.text(metric.label, centerX, cardY + 60, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.4);
    doc.setTextColor(112, 117, 131);
    const periodLines = doc.splitTextToSize(metric.period, cardWidth - 16);
    doc.text(periodLines, centerX, cardY + 79, {
      align: "center",
      lineHeightFactor: 1.2,
    });
    if (metric.unit) {
      doc.text(metric.unit, centerX, cardY + 101, { align: "center" });
    }
  });

  const notesY = cardsY + cardsHeight + 22;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(45, 51, 73);
  doc.text("# Data points from BSE", margin + padding, notesY);
  doc.setFont("helvetica", "normal");
  doc.text(
    "# Updates every 15 minutes during market hours",
    margin + padding,
    notesY + 14
  );
  doc.text(`# Last Updated: ${display.lastUpdated}`, margin + padding, notesY + 28);

  const nextY = y + sectionHeight;
  (doc as any).__nextY = nextY;
  return { doc, nextY };
};
